import { normalizeMongoId } from "./normalizeMongoId";
import {
  emailUsernameFromEmail,
  sanitizeProfileValue,
} from "./customerProfileUtils";
import { formatDisplayTitle } from "./landingUtils";

export function chatPeerDisplayName(user, fallback = "User") {
  if (!user) return fallback;

  const company = sanitizeProfileValue(user.company_name);
  if (company) return formatDisplayTitle(company, fallback);

  const fullName = sanitizeProfileValue(user.full_name);
  if (fullName) return formatDisplayTitle(fullName, fallback);

  const name = sanitizeProfileValue(user.name);
  if (name) return formatDisplayTitle(name, fallback);

  const email = sanitizeProfileValue(user.email);
  if (email) return formatDisplayTitle(emailUsernameFromEmail(email), fallback);

  return fallback;
}

export function normalizeChatUserId(id) {
  if (id == null) return "";
  if (typeof id === "object" && id._id != null) {
    return normalizeMongoId(id._id);
  }
  return normalizeMongoId(id);
}

export function getChatPeerId(chat, currentUserId) {
  if (!chat || !currentUserId) return null;
  const me = normalizeChatUserId(currentUserId);
  if (normalizeChatUserId(chat.sender_id) === me) {
    return normalizeChatUserId(chat.receiver_id) || null;
  }
  if (normalizeChatUserId(chat.receiver_id) === me) {
    return normalizeChatUserId(chat.sender_id) || null;
  }
  return (
    normalizeChatUserId(chat.receiver_id) ||
    normalizeChatUserId(chat.sender_id) ||
    null
  );
}

export function normalizeChatMessages(payload) {
  const rows = extractChatMessageRows(payload);
  return rows.map((message) => ({
    ...message,
    sender_id: normalizeChatUserId(message?.sender_id),
    receiver_id: normalizeChatUserId(message?.receiver_id),
  }));
}

export function extractChatMessageRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.getdata)) return payload.getdata;
  return [];
}

export function extractChatMessagesFromResponse(body) {
  return normalizeChatMessages(body);
}

export function isMessageForChat(message, senderId, receiverId) {
  if (!message) return false;
  const me = normalizeChatUserId(senderId);
  const peer = normalizeChatUserId(receiverId);
  const messageSender = normalizeChatUserId(message.sender_id);
  const messageReceiver = normalizeChatUserId(message.receiver_id);
  return (
    (messageSender === peer && messageReceiver === me) ||
    (messageSender === me && messageReceiver === peer)
  );
}

function parseStructuredMessage(raw) {
  const text = String(raw || "").trim();
  if (!text.startsWith("{") || !text.endsWith("}")) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function formatMessagePreview(raw, { messageType } = {}) {
  if (Number(messageType) === 1) return "Image";

  const structured = parseStructuredMessage(raw);
  if (structured) {
    if (structured.image || structured.imageUrl || structured.url) return "Image";
    if (
      structured.latitude != null ||
      structured.lat != null ||
      structured.longitude != null ||
      structured.lng != null
    ) {
      return "Location";
    }
  }

  const text = String(raw || "").trim();
  if (!text) return "No messages yet";
  return text.length > 25 ? `${text.slice(0, 25)}...` : text;
}

export function getLastMessagePreview(lastMessage) {
  if (!lastMessage) return "No messages yet";
  return formatMessagePreview(lastMessage.message, {
    messageType: lastMessage.message_type,
  });
}
